import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    IconButton,
    TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

interface InputDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (value: string) => void;
    title: string;
    description?: string;
    loading?: boolean;
    defaultValue?: string;
    color?: "primary" | "success" | "warning" | "error";
}

const CustomInputDialog: React.FC<InputDialogProps> = ({
    open,
    onClose,
    onSubmit,
    title,
    description,
    loading = false,
    defaultValue = "",
    color = "primary",
}) => {
    const [inputValue, setInputValue] = useState(defaultValue);

    useEffect(() => {
        if (open) setInputValue(defaultValue);
    }, [open, defaultValue]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="input-dialog-title"
            sx={{
                "& .MuiDialog-paper": {
                    width: "100%",
                    maxWidth: 480,
                    borderRadius: 3,
                    backgroundColor: "#fff",
                    px: 2,
                },
            }}
        >
            <DialogTitle
                id="input-dialog-title"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    textAlign: "center",
                    pt: 4,
                    pb: 2,
                }}
            >
                <WarningAmberRoundedIcon color={color} sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <img alt="Close icon" src="/x.png" height="18" />
            </IconButton>

            <DialogContent sx={{ textAlign: "center", py: 2 }}>
                {description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {description}
                    </Typography>
                )}
                <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    variant="outlined"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Enter your input here..."
                />
            </DialogContent>

            <DialogActions sx={{ flexDirection: "column", gap: 1.5, pb: 3, px: 4 }}>
                <LoadingButton
                    onClick={() => onSubmit(inputValue)}
                    loading={loading}
                    variant="contained"
                    color={color}
                    fullWidth
                    sx={{
                        textTransform: "none",
                        borderRadius: 20,
                        fontWeight: 600,
                        py: 1.2,
                        fontSize: "1rem",
                    }}
                >
                    Submit
                </LoadingButton>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    color="inherit"
                    fullWidth
                    sx={{
                        textTransform: "none",
                        borderRadius: 20,
                        py: 1.2,
                        fontWeight: 500,
                        fontSize: "1rem",
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomInputDialog;
